import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { AwmsState, ColumnFilterState, SearchFilter, SearchFilterState, SelectedStaffState, SheetType } from './types';
import { initialState } from './stateHelper';
import { getActiveYear, getYears } from '#server/utils/date-utils';

export interface Actions {
  activeYear: number;
  getYears: () => string[];
  setActiveYear: (year: number) => void;
  selectedSchoolId: number;
  setSelectedSchoolId: (selectedSchoolId: number) => void;
  selectedStudyPeriodId: number;
  setSelectedStudyPeriodId: (selectedStudyPeriodId: number) => void;
  selectedCampusId: number;
  setSelectedCampusId: (selectedTdCampusId: number) => void;
  taughtWeeks: number;
  setTaughtWeeks: (taughtWeeks: number) => void;
  plannedStudents: number;
  setPlannedStudents: (plannedStudents: number) => void;
  selectedDisciplineId: number;
  setSelectedDisciplineId: (selectedDisciplineId: number) => void;
  selectedEmpStatusAbbr: SearchFilter[];
  setSelectedEmpStatusAbbr: (selectedEmpStatusAbbr: SearchFilter[]) => void;
  searchFilters: SearchFilterState;
  setSearchFilters: (newFilters: SearchFilterState) => void;
  columnFilters: ColumnFilterState; // this filter is being used for the GlobalFilter
  setColumnFilterState: (columnFilters: ColumnFilterState) => void;
  selectedStaffId: number;
  setSelectedStaffId: (staffId: number) => void;
  selectedStaff: SelectedStaffState;
  setSelectedStaff: (selectedStaff: SelectedStaffState) => void;
  setActiveSheet: (activeSheet: SheetType | null) => void;
  setLastUwId: (lastUwId: number) => void;
  resetState: () => void;
}

export const useAwmsStore = create<AwmsState & Actions>()(
  devtools(
    immer<AwmsState & Actions>((set, get) => ({
      activeYear: getActiveYear(),
      getYears: () => getYears(),
      setActiveYear: (year) => {
        set((state) => {
          state.activeYear = year;
        });
      },
      selectedSchoolId: 0,
      setSelectedSchoolId: (schoolId) => {
        set((state) => {
          state.selectedSchoolId = schoolId;
        });
      },
      selectedStudyPeriodId: 0,
      setSelectedStudyPeriodId(selectedStudyPeriodId) {
        set((state) => {
          state.selectedStudyPeriodId = selectedStudyPeriodId;
        });
      },
      selectedCampusId: 0,
      searchFilters: {},
      setSelectedCampusId: (campusId) => {
        set((state) => {
          state.selectedCampusId = campusId;
        });
      },
      taughtWeeks: 10,
      setTaughtWeeks: (taughtWeeks) => {
        set((state) => {
          state.taughtWeeks = taughtWeeks;
        });
      },
      plannedStudents: 15,
      setPlannedStudents: (plannedStudents) => {
        set((state) => {
          state.plannedStudents = plannedStudents;
        });
      },
      selectedEmpStatusAbbr: [],
      setSelectedEmpStatusAbbr: (empStatusAbbr: SearchFilter[]) => {
        set((state) => {
          state.selectedEmpStatusAbbr = empStatusAbbr;
        });
      },
      selectedDisciplineId: 0,
      setSelectedDisciplineId: (disciplineId) => {
        set((state) => {
          state.selectedDisciplineId = disciplineId;
        });
      },
      setSearchFilters: (newFilters: Partial<SearchFilterState>) => {
        set((state) => {
          Object.keys(newFilters).forEach((key) => {
            const typedKey = key as keyof SearchFilterState; // Ensure key is a valid key of SearchFilterState
            if (newFilters[typedKey] === undefined) {
              delete state.searchFilters[typedKey]; // Remove the property from state
            } else {
              (state.searchFilters as any)[typedKey] = newFilters[typedKey]; // Add or update the property
            }
          });
        });
      },
      columnFilters: {},
      setColumnFilterState: (columnFilters) => {
        set((state) => {
          state.columnFilters = columnFilters;
        });
      },
      selectedStaffId: 0,
      setSelectedStaffId: (staffId: number) => {
        set((state) => {
          state.selectedStaffId = staffId;
        });
      },
      selectedStaff: {},
      setSelectedStaff: (selectedStaff: SelectedStaffState) => {
        set((state) => {
          state.selectedStaff = selectedStaff;
        });
      },
      activeSheet: null,
      setActiveSheet: (activeSheet: SheetType | null) => {
        set((state) => {
          state.activeSheet = activeSheet;
        });
      },
      lastUwId: 0,
      setLastUwId: (lastUwId: number) => {
        set((state) => {
          state.lastUwId = lastUwId;
        });
      },
      resetState: () => set(() => ({ ...initialState })),
    })),
  ),
);
